/**
 * Superadmin cache-efficacy telemetry dashboard.
 *
 * Line charts for three cache-quality axes, each with a recent-failures
 * drill-down:
 *   1. Compile validity (write time) — valid vs invalid cache compiles.
 *   2. Node cache miss rate (read time) + L1/L2/L3 execution tier mix.
 *   3. Step miss rate by locator strategy (deterministic vs agentic) + action tier.
 *
 * Data accrues from deploy time forward (the compile-events table and the
 * structured replay-timing fields are new) — empty windows before then are
 * expected. Recovery/replay splits elsewhere can be stale; not relied on here.
 *
 * @module pages/superadmin/cache-telemetry/CacheTelemetryDashboard
 */
import React from 'react';
import {
  Box,
  Flex,
  Heading,
  Text,
  Card,
  CardContent,
  Select,
  LineChart,
  ChartEmptyState,
  LoadingOrbit,
  Button,
} from '@chatsdet/ui';
import { useCacheTelemetrySearch } from './useCacheTelemetrySearch';
import { useCacheTelemetryTrends, useCacheTelemetryDrillDown } from './useCacheTelemetryData';

const DAYS_OPTIONS = [
  { id: '7', label: '7 days' },
  { id: '14', label: '14 days' },
  { id: '30', label: '30 days' },
  { id: '90', label: '90 days' },
];

// Accessibility-aware palette (see memory: pass=blue, fail=orange).
const BLUE = 'hsl(var(--success))';
const ORANGE = 'hsl(var(--warning))';
const RED = 'hsl(var(--destructive))';

const pct = (num: number, denom: number) => (denom > 0 ? Math.round((num / denom) * 1000) / 10 : 0);

function ChartCard({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <Card padding="md">
      <CardContent>
        <Heading level={5} className="mb-1">
          {title}
        </Heading>
        {subtitle && (
          <Text size="sm" color="muted" className="mb-3 block">
            {subtitle}
          </Text>
        )}
        {children}
      </CardContent>
    </Card>
  );
}

export function CacheTelemetryDashboard() {
  const { days, updateSearch, search } = useCacheTelemetrySearch();
  const workflowId = search.workflowId;

  const compile = useCacheTelemetryTrends('compile_validity', { days, workflowId });
  const nodeMiss = useCacheTelemetryTrends('node_miss', { days, workflowId });
  const execTier = useCacheTelemetryTrends('exec_tier', { days, workflowId });
  const stepStrategy = useCacheTelemetryTrends('step_strategy', { days, workflowId });
  const actionTier = useCacheTelemetryTrends('action_tier', { days, workflowId });

  // ── Transforms: counts → percentages for the rate charts ───────────────────
  const compileData = React.useMemo(
    () =>
      (compile.data ?? []).map((d) => ({
        date: d.date,
        validPct: pct(d.valid, d.valid + d.invalid),
        valid: d.valid,
        invalid: d.invalid,
      })),
    [compile.data]
  );

  const nodeMissData = React.useMemo(
    () =>
      (nodeMiss.data ?? []).map((d) => ({
        date: d.date,
        missPct: pct(d.missed, d.cachePresentTotal),
        missed: d.missed,
      })),
    [nodeMiss.data]
  );

  const execTierData = React.useMemo(
    () =>
      (execTier.data ?? []).map((d) => ({
        date: d.date,
        l1Cache: d.l1Cache,
        l2Recovery: d.l2Recovery,
        l3Agent: d.l3Agent,
      })),
    [execTier.data]
  );

  const stepStrategyData = React.useMemo(
    () =>
      (stepStrategy.data ?? []).map((d) => ({
        date: d.date,
        deterministicFailPct: pct(d.deterministicFailed, d.deterministicTotal),
        agenticFailPct: pct(d.agenticFailed, d.agenticTotal),
      })),
    [stepStrategy.data]
  );

  const actionTierData = React.useMemo(
    () =>
      (actionTier.data ?? []).map((d) => ({
        date: d.date,
        cacheReplay: d.cacheReplay,
        agent: d.agent,
      })),
    [actionTier.data]
  );

  // ── Inline summary (latest bucket) ─────────────────────────────────────────
  const latest = <T,>(arr: T[]): T | undefined => arr[arr.length - 1];
  const latestValid = latest(compileData);
  const latestMiss = latest(nodeMissData);
  const summary = [
    latestValid ? `compile validity ${latestValid.validPct}%` : null,
    latestMiss ? `node miss ${latestMiss.missPct}%` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  const anyLoading =
    compile.isLoading ||
    nodeMiss.isLoading ||
    execTier.isLoading ||
    stepStrategy.isLoading ||
    actionTier.isLoading;

  return (
    <Box className="p-6">
      <Flex justify="between" align="start" className="mb-6">
        <Box>
          <Heading level={3}>Cache Telemetry</Heading>
          <Text color="muted" className="block mt-1">
            Workflow cache efficacy across compile, read, and step-replay.
            {summary ? ` Latest: ${summary}.` : ''}
          </Text>
        </Box>
        <Select
          placeholder="Time range"
          items={DAYS_OPTIONS}
          selectedKey={String(days)}
          onSelectionChange={(key) => updateSearch({ days: Number(key) })}
          className="w-[140px]"
        />
      </Flex>

      {anyLoading ? (
        <Flex justify="center" className="py-16">
          <LoadingOrbit />
        </Flex>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ChartCard
            title="Compile Validity (write time)"
            subtitle="% of freshly minted caches that are fully replayable (deterministic, no gaps, no agent-required actions). Accrues from deploy."
          >
            {compileData.length > 0 ? (
              <LineChart
                data={compileData}
                xDataKey="date"
                lines={[{ dataKey: 'validPct', name: 'Valid %', color: BLUE }]}
                height={250}
                connectNulls
              />
            ) : (
              <ChartEmptyState message="No compile events in this window yet." />
            )}
          </ChartCard>

          <ChartCard
            title="Node Cache Miss Rate (read time)"
            subtitle="% of times a cache existed for an action/assertion node but the lookup missed it (URL-shape / invalid / legacy hash mismatch). Excludes never-cached nodes and replay fallbacks."
          >
            {nodeMissData.length > 0 ? (
              <LineChart
                data={nodeMissData}
                xDataKey="date"
                lines={[{ dataKey: 'missPct', name: 'Miss %', color: ORANGE }]}
                height={250}
                connectNulls
              />
            ) : (
              <ChartEmptyState message="No cache-eligible steps in this window." />
            )}
          </ChartCard>

          <ChartCard
            title="Execution Tier Mix"
            subtitle="Nodes executed per day by tier (usage_events). L3 includes nodes with no cache — read alongside miss rate, not as the miss rate."
          >
            {execTierData.length > 0 ? (
              <LineChart
                data={execTierData}
                xDataKey="date"
                lines={[
                  { dataKey: 'l1Cache', name: 'L1 cache', color: BLUE },
                  { dataKey: 'l2Recovery', name: 'L2 recovery', color: ORANGE },
                  { dataKey: 'l3Agent', name: 'L3 agent', color: RED },
                ]}
                height={250}
                connectNulls
              />
            ) : (
              <ChartEmptyState message="No node executions in this window." />
            )}
          </ChartCard>

          <ChartCard
            title="Step Miss Rate by Strategy"
            subtitle="% of cached actions that failed, split by the locator that was attempted: deterministic vs agentic."
          >
            {stepStrategyData.length > 0 ? (
              <LineChart
                data={stepStrategyData}
                xDataKey="date"
                lines={[
                  { dataKey: 'deterministicFailPct', name: 'Deterministic fail %', color: ORANGE },
                  { dataKey: 'agenticFailPct', name: 'Agentic fail %', color: RED },
                ]}
                height={250}
                showLegend
                connectNulls
              />
            ) : (
              <ChartEmptyState message="No cached replay actions in this window." />
            )}
          </ChartCard>

          <ChartCard
            title="Action Tier Mix"
            subtitle="Browser actions per day executed via cache replay vs the agent (usage_events)."
          >
            {actionTierData.length > 0 ? (
              <LineChart
                data={actionTierData}
                xDataKey="date"
                lines={[
                  { dataKey: 'cacheReplay', name: 'Cache replay', color: BLUE },
                  { dataKey: 'agent', name: 'Agent', color: ORANGE },
                ]}
                height={250}
                showLegend
                connectNulls
              />
            ) : (
              <ChartEmptyState message="No browser actions in this window." />
            )}
          </ChartCard>
        </div>
      )}

      <Heading level={4} className="mt-10 mb-4">
        Recent Failures
      </Heading>
      <div className="grid grid-cols-1 gap-6">
        <CompileFailuresPanel days={days} workflowId={workflowId} />
        <NodeMissesPanel days={days} workflowId={workflowId} />
        <StepStrategyFailuresPanel days={days} workflowId={workflowId} />
      </div>
    </Box>
  );
}

// ── Drill-down panels (lazy: fetch only when expanded) ────────────────────────

function DrillDownShell({
  title,
  expanded,
  onToggle,
  children,
}: {
  title: string;
  expanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <Card padding="md">
      <CardContent>
        <Flex justify="between" align="center" className={expanded ? 'mb-4' : ''}>
          <Heading level={5}>{title}</Heading>
          <Button variant="secondary" size="sm" onPress={onToggle}>
            {expanded ? 'Hide' : 'Show recent'}
          </Button>
        </Flex>
        {expanded && children}
      </CardContent>
    </Card>
  );
}

function Table({ head, rows }: { head: string[]; rows: React.ReactNode[][] }) {
  if (rows.length === 0) {
    return (
      <Text size="sm" color="muted">
        No failures in this window.
      </Text>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-muted-foreground border-b">
            {head.map((h) => (
              <th key={h} className="py-2 pr-4 font-medium">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((cells, i) => (
            <tr key={i} className="border-b last:border-0">
              {cells.map((c, j) => (
                <td key={j} className="py-2 pr-4 font-mono text-xs align-top">
                  {c}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const short = (id: string | null | undefined) => (id ? id.slice(0, 8) : '—');
const dateOnly = (iso: string) => iso.replace('T', ' ').replace('Z', '').slice(0, 19);

function CompileFailuresPanel({ days, workflowId }: { days: number; workflowId?: string }) {
  const [expanded, setExpanded] = React.useState(false);
  const q = useCacheTelemetryDrillDown('compile-failures', { days, workflowId, enabled: expanded });
  return (
    <DrillDownShell title="Compile failures" expanded={expanded} onToggle={() => setExpanded((v) => !v)}>
      {q.isLoading ? (
        <LoadingOrbit />
      ) : (
        <Table
          head={['When', 'Workflow', 'Node', 'Reason', 'requiresAgent']}
          rows={(q.data ?? []).map((r) => [
            dateOnly(r.createdAt),
            short(r.workflowId),
            short(r.nodeId),
            r.failureReason ?? '—',
            String(r.requiresAgentCount),
          ])}
        />
      )}
    </DrillDownShell>
  );
}

function NodeMissesPanel({ days, workflowId }: { days: number; workflowId?: string }) {
  const [expanded, setExpanded] = React.useState(false);
  const q = useCacheTelemetryDrillDown('node-misses', { days, workflowId, enabled: expanded });
  return (
    <DrillDownShell title="Node cache misses" expanded={expanded} onToggle={() => setExpanded((v) => !v)}>
      {q.isLoading ? (
        <LoadingOrbit />
      ) : (
        <Table
          head={['When', 'Run', 'Node', 'Type', 'Outcome', 'Reason']}
          rows={(q.data ?? []).map((r) => [
            dateOnly(r.createdAt),
            short(r.runId),
            short(r.nodeId),
            r.nodeType,
            r.cacheOutcome,
            r.cacheOutcomeReason ?? '—',
          ])}
        />
      )}
    </DrillDownShell>
  );
}

function StepStrategyFailuresPanel({ days, workflowId }: { days: number; workflowId?: string }) {
  const [expanded, setExpanded] = React.useState(false);
  const q = useCacheTelemetryDrillDown('step-strategy-failures', {
    days,
    workflowId,
    enabled: expanded,
  });
  return (
    <DrillDownShell
      title="Step replay failures"
      expanded={expanded}
      onToggle={() => setExpanded((v) => !v)}
    >
      {q.isLoading ? (
        <LoadingOrbit />
      ) : (
        <Table
          head={['When', 'Workflow', 'Action #', 'Action', 'Element', 'Page']}
          rows={(q.data ?? []).map((r) => [
            dateOnly(r.createdAt),
            short(r.workflowId),
            String(r.failedActionIndex),
            r.failedActionType,
            r.elementDescription ?? '—',
            r.pageUrl ?? '—',
          ])}
        />
      )}
    </DrillDownShell>
  );
}
